import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Check, X, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

// Recréer une interface minimale pour les features ici
interface ComparisonFeature {
  name: string;
  teaser: boolean;
  regular: boolean;
  vip: boolean;
  description?: string; // Description pour le tooltip
}

interface PricingComparisonTableProps {
  features: ComparisonFeature[];
  // Utiliser des clés de traduction pour les titres de plans pour la flexibilité
  planTitleKeys: {
    teaser: string; // e.g., 'subscription.teaser.title'
    regular: string;
    vip: string;
  };
}

const PricingComparisonTable: React.FC<PricingComparisonTableProps> = ({ features, planTitleKeys }) => {
  const { t } = useTranslation();

  const renderCheck = (available: boolean) => {
    return available ? (
      <Check className="h-5 w-5 text-green-500 mx-auto" />
    ) : (
      <X className="h-5 w-5 text-muted-foreground mx-auto" />
    );
  };

  return (
    <div className="mt-16">
      <h2 className="text-3xl font-bold text-center mb-8">{t('pricing.detailed_comparison')}</h2>
      <Table className="border">
        <TableHeader>
          <TableRow>
            <TableHead className="w-2/5">{t('pricing.feature')}</TableHead>
            <TableHead className="text-center">{t(planTitleKeys.teaser)}</TableHead>
            <TableHead className="text-center font-bold border-x">{t(planTitleKeys.regular)}</TableHead>
            <TableHead className="text-center">{t(planTitleKeys.vip)}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {features.map((feature, index) => (
            <TableRow key={index} className="hover:bg-muted/20">
              <TableCell className="font-medium flex items-center">
                <span>{feature.name}</span>
                {feature.description && (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 ml-2 text-muted-foreground">
                          <Info className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{feature.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </TableCell>
              <TableCell className="text-center">{renderCheck(feature.teaser)}</TableCell>
              <TableCell className="text-center border-x">{renderCheck(feature.regular)}</TableCell>
              <TableCell className="text-center">{renderCheck(feature.vip)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PricingComparisonTable; 